import React from 'react';
import { PermissionsCheck } from '../../Components/Auth/PermissionsCheck';
import { ButtonGroup } from '../../Components/ButtonGroup';
import { DeleteDialog } from '../../Components/DeleteDialog/DeleteDialog';
import { DraftPublishedChipStatus } from '../../Components/HeaderWithControls/DraftPublishedChip';
import { HeaderWithControls } from '../../Components/HeaderWithControls/HeaderWithControls';
import { InfoIcon } from '../../Components/Icons';
import { LayoutCentral } from '../../Components/LayoutCentral';
import { LoadingPlaceholder } from '../../Components/Placeholder';
import { ListBulkAction } from '../../Contracts/MedicationListModels';
import { MedicationListStatus } from '../../Contracts/MedicationListsSearchFilter.Request';
import { usePermissions } from '../../Hooks/usePermissions';
import { MedicationsPermissions } from '../../MedicationsPermissions';
import { divider, info } from '../../Styles/Colours';
import { MedicationListDialogBulkActionWizard } from '../MedicationListBulkActionDialog/MedicationListDialogBulkActionWizard';
import { DeleteMedicationListButton } from './DeleteMedicationListButton';
import { EditMedicationListButton } from './EditMedicationListButton';
import styles from './MedicationList.module.scss';
import { MedicationListBulkActionButton } from './MedicationListBulkActionButton';
import { MedicationListEntries } from './MedicationListEntries';
import { PublishButton } from './PublishButton';
import { EntryLoadingState, MedListLoadingState, useMedicationListState } from './UseMedicationListState';

export const MedicationList = (props: { medListId: string; onDelete(): void }) => {
    const { state, autoSaveStatus, ...methods } = useMedicationListState(props);

    const permissions = usePermissions({
        write: MedicationsPermissions.WriteMedicationLists
    });

    if (!!state.medicationList && state.medicationListLoadingState === MedListLoadingState.Loaded) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.listContent}>
                    <HeaderWithControls
                        title={state.medicationList.name}
                        autoSaveIndicator={
                            permissions.write
                                ? {
                                      status: autoSaveStatus
                                  }
                                : undefined
                        }
                        draftPublishedChipStatus={
                            state.medicationList.status === MedicationListStatus.Draft
                                ? DraftPublishedChipStatus.Draft
                                : DraftPublishedChipStatus.Published
                        }
                        updatedOn={state.medicationList.lastUpdatedOn}
                        updatedBy={state.medicationList.lastUpdatedBy}
                        buttons={
                            <PermissionsCheck permission={MedicationsPermissions.WriteMedicationLists}>
                                <ButtonGroup borderColour={divider}>
                                    <EditMedicationListButton
                                        medicationList={state.medicationList}
                                        testId='medication-list-edit'
                                        onComplete={methods.reloadMedicationList}
                                        disabled={!!state.editRowId}
                                    />
                                    <MedicationListBulkActionButton
                                        medicationList={state.medicationList}
                                        testId='mediation-list-bulk-action'
                                        onBulkActionClicked={methods.showBulkActionWizard}
                                        disabled={!!state.editRowId}
                                    />
                                    <DeleteMedicationListButton
                                        medicationList={state.medicationList}
                                        testId='medication-list-delete'
                                        onDelete={props.onDelete}
                                        disabled={!!state.editRowId}
                                    />
                                </ButtonGroup>
                                <PublishButton
                                    currentMedicationList={state.medicationList}
                                    onToggled={methods.handlePublishedToggled}
                                    disabled={!!state.editRowId}
                                />
                            </PermissionsCheck>
                        }
                    />
                    {state.entryLoadingState === EntryLoadingState.Loaded ||
                    state.entryLoadingState === EntryLoadingState.Refreshing ? (
                        <MedicationListEntries
                            medListState={state}
                            onRowEditStart={methods.startRowEditing}
                            onRowEditCancelled={methods.cancelRowEditing}
                            onSearch={methods.updateQuery}
                            onSaveMedication={methods.handleSaveMedication}
                            onDeleteMedication={methods.showDeleteMedicationDialog}
                            onAddAction={() => methods.showBulkActionWizard(ListBulkAction.Append)}
                        />
                    ) : (
                        <LoadingPlaceholder />
                    )}

                    {state.takeBulkAction && (
                        <MedicationListDialogBulkActionWizard
                            medListId={props.medListId}
                            bulkAction={state.takeBulkAction}
                            onClose={methods.cancelBulkAction}
                            onComplete={methods.bulkActionComplete}
                        />
                    )}
                    {!!state.deleteRow && (
                        <DeleteDialog
                            title='Delete medication list entry'
                            testId='delete-medication-entry'
                            disabled={state.deletingRow}
                            onCancel={methods.cancelDeleteMedication}
                            onOk={methods.handleDeleteMedication}
                        >
                            Are you sure you want to remove {state.deleteRow?.medication?.name ?? 'this entry'}?
                        </DeleteDialog>
                    )}
                </div>
                {state.entries.length > 100 && (
                    <div className={styles.searchResultsLimited}>
                        <InfoIcon color={info} size={'16'} noPadding style={{ marginRight: 14 }} />
                        Showing top 100 results. To view more, search the medication list table.
                    </div>
                )}
            </div>
        );
    }

    return (
        <LayoutCentral>
            <LoadingPlaceholder />
        </LayoutCentral>
    );
};
